import { Button, Divider, Form, message, Row, Steps } from 'antd';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SimcardsOnlineGraphTypes,
  TransactionsPayGraphTypes,
  AlarmTriggerOptions,
  AlarmGraphAppNameOptions,
  alarmGraphSimcardOptions,
} from '@utils/alarmsConstants';
import SessionContext from '@providers/Session/SessionContext';
import useSwr from '@hooks/useSwr';
import CustomTag from '@components/CustomTag';
import { CreateAlarmStepTwo } from './CreateAlarmSteps/CreateAlarmStepTwo/CreateAlarmStepTwo';
import { CreateAlarmStepOne } from './CreateAlarmSteps/CreateAlarmStepOne/CreateAlarmStepOne';
import { CustomModalWrapper } from './styles';

interface PreSelectedValues {
  tipoGrafico?: string;
  appName?: string;
  graphContext?: string;
  skipToStepTwo?: boolean;
  [key: string]: any;
}

interface CreateAlarmStepsProps {
  visible: boolean;
  onClose: () => void;
  preSelectedValues?: PreSelectedValues;
  readOnlyFields?: string[];
  onAlarmCreated?: () => void;
  hideSteps?: boolean;
}

interface FormValues {
  tipoGrafico: string;
  produtos?: string[];
  tecnologias?: string[];
  operadoras?: string[];
  estados?: string[];
  macroregioes?: string[];
  averageTime?: string;
  transactionByStatus?: string;
  transactionByStatusStatus?: string;
  TransactionHistory?: string;
  transactionHistoryStatus?: string;
  transactionStatus?: string;
  transactionStatusStatus?: string;
  transactionByType?: string;
  transactionByTypeStatus?: string;
  averageTimeThreshold?: number;
  transactionStatusIds?: string[];
  transactionHistoryPeriod?: string;
  transactionStatusThreshold?: number;
  transactionTypes?: string[];
  tipoAlarme?: string;
  thresholdMeasure?: string;
  thresholdValue?: number;
  percentVariationMeasure?: string;
  percentVariationPeriod?: string;
  percentVariationValue?: number;
  seasonalMeasure?: string;
  seasonalMoment?: string;
  seasonalTime?: any;
  seasonalValue?: number;
  nomeAlarme?: string;
  notificationChannels?: string[];
  telegramIds?: string[];
  emails?: string[];
}

interface CreateAlarmResponse {
  success: boolean;
  message?: string;
  data?: {
    idAlarme: string;
    nomeAlarme: string;
    nomeGrafico: string;
    customerId: number;
    status: string;
    dataCriacao: string;
    dataUltimaAlteracao: string;
  };
}

interface AlarmSubmitData {
  nomeAlarme: string;
  nomeGrafico: string;
  qtdMax: number | null;
  qtdMin: number | null;
  chatIds: string[] | null;
  emails: string[] | null;
  produtos: string[] | null;
  operadoras: string[] | null;
  tecnologias: string[] | null;
  estados: string[] | null;
  macroRegioes: string[] | null;
  tipoAlarme: string;
  thresholdMeasure?: string;
  percentVariationMeasure?: string;
  seasonalMeasure?: string;
  lastTime?: string | null;
  momento?: string | null;
  hora?: number | null;
  minuto?: number | null;
}

export function CreateAlarmSteps({
  visible,
  onClose,
  preSelectedValues,
  readOnlyFields = [],
  onAlarmCreated,
  hideSteps = false,
}: CreateAlarmStepsProps) {
  const { t } = useTranslation('create-alarm-step');
  const [form] = Form.useForm<FormValues>();
  const [current, setCurrent] = useState(0);
  const { customer } = useContext(SessionContext);
  const [skipToStepTwo, setSkipToStepTwo] = useState(false);
  const [submitData, setSubmitData] = useState<AlarmSubmitData | null>(null);

  const steps = [
    {
      title: t('create-alarm-step:createAlarmStepOne'),
    },
    {
      title: t('create-alarm-step:createAlarmStepTwo'),
    },
  ];

  const processPreSelectedValues = (values: PreSelectedValues) => {
    const processed = { ...values };

    if (!processed.tipoGrafico && processed.graphContext) {
      processed.tipoGrafico = processed.graphContext;
      processed.readOnlyFields = ['tipoGrafico'];
    }

    return processed;
  };

  useEffect(() => {
    if (visible && preSelectedValues) {
      const processedValues = processPreSelectedValues(preSelectedValues);
      form.setFieldsValue(processedValues);

      const isTransactionTypeWithStatusField = [
        TransactionsPayGraphTypes.TRANSACTIONS_BY_STATUS,
        TransactionsPayGraphTypes.TRANSACTION_HISTORY,
        TransactionsPayGraphTypes.TRANSACTION_STATUS,
        TransactionsPayGraphTypes.TRANSACTIONS_BY_TYPE,
      ].includes(processedValues.tipoGrafico as TransactionsPayGraphTypes);

      if (processedValues.skipToStepTwo && !isTransactionTypeWithStatusField) {
        setSkipToStepTwo(true);
        setCurrent(1);
      } else {
        setSkipToStepTwo(false);
        setCurrent(0);
      }
    } else if (visible) {
      setSkipToStepTwo(false);
      setCurrent(0);
    }
  }, [visible, preSelectedValues, form]);

  const getSimcardsOnlineFieldsToValidate = (graphType: string): string[] => {
    switch (graphType) {
      case SimcardsOnlineGraphTypes.PRODUCT_GRAPH:
        return ['produtos'];
      case SimcardsOnlineGraphTypes.TECHNOLOGY_GRAPH:
        return ['tecnologias'];
      case SimcardsOnlineGraphTypes.OPERATOR_GRAPH:
        return ['operadoras'];
      case SimcardsOnlineGraphTypes.STATE_GRAPH:
        return ['estados'];
      case SimcardsOnlineGraphTypes.MACROREGIAO_GRAPH:
        return ['macroregioes'];
      default:
        return [];
    }
  };

  const getTransactionsPayFieldsToValidate = (graphType: string): string[] => {
    const formValues = form.getFieldsValue();
    switch (graphType) {
      case TransactionsPayGraphTypes.AVERAGE_TIME:
        return ['averageTime'];
      case TransactionsPayGraphTypes.TRANSACTIONS_BY_STATUS: {
        const fields = ['transactionByStatus'];
        if (formValues.transactionByStatus) {
          fields.push('transactionByStatusStatus');
        }
        return fields;
      }
      case TransactionsPayGraphTypes.TRANSACTION_HISTORY: {
        const historyFields = ['TransactionHistory'];
        if (formValues.TransactionHistory) {
          historyFields.push('transactionHistoryStatus');
        }
        return historyFields;
      }
      case TransactionsPayGraphTypes.TRANSACTION_STATUS: {
        const statusFields = ['transactionStatus'];
        if (formValues.transactionStatus) {
          statusFields.push('transactionStatusStatus');
        }
        return statusFields;
      }
      case TransactionsPayGraphTypes.TRANSACTIONS_BY_TYPE: {
        const typeFields = ['transactionByType'];
        if (formValues.transactionByType) {
          typeFields.push('transactionByTypeStatus');
        }
        return typeFields;
      }
      default:
        return [];
    }
  };

  const next = async () => {
    try {
      let fieldsToValidate = ['tipoGrafico'];

      if (current === 0) {
        const tipoGrafico = form.getFieldValue('tipoGrafico');

        if (tipoGrafico) {
          const isSimcardsOnlineType = Object.values(
            SimcardsOnlineGraphTypes,
          ).includes(tipoGrafico);
          if (isSimcardsOnlineType) {
            fieldsToValidate = [
              ...fieldsToValidate,
              ...getSimcardsOnlineFieldsToValidate(tipoGrafico),
            ];
          }

          const isTransactionsPayType = Object.values(
            TransactionsPayGraphTypes,
          ).includes(tipoGrafico);
          if (isTransactionsPayType) {
            fieldsToValidate = [
              ...fieldsToValidate,
              ...getTransactionsPayFieldsToValidate(tipoGrafico),
            ];
          }
        }
      } else if (current === 1) {
        fieldsToValidate = ['nomeAlarme', 'tipoAlarme', 'notificationChannels'];

        const tipoAlarme = form.getFieldValue('tipoAlarme');
        if (tipoAlarme === AlarmTriggerOptions.THRESHOLD) {
          fieldsToValidate.push('thresholdMeasure', 'thresholdValue');
        }
      }

      await form.validateFields(fieldsToValidate);

      setCurrent(current + 1);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const getAlarmEndpoint = () => {
    const formValues = form.getFieldsValue();
    const isTransactionType = Object.values(TransactionsPayGraphTypes).includes(
      formValues.tipoGrafico as TransactionsPayGraphTypes,
    );

    let measure = '';
    if (formValues.tipoAlarme === 'threshold') {
      measure = formValues.thresholdMeasure || '';
    } else if (formValues.tipoAlarme === 'variacao_percentual') {
      measure = formValues.percentVariationMeasure || '';
    } else if (formValues.tipoAlarme === 'sazonal') {
      measure = formValues.seasonalMeasure || '';
    }

    const baseUrl = isTransactionType
      ? '/service-proxy/dashboard/transacao/alarme/kapacitor/alerta_task'
      : '/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task';

    return `${baseUrl}/${measure}/salvar`;
  };

  const buildPayload = (values: FormValues) => {
    const simcardsGraphTypeMap = {
      'by-product': alarmGraphSimcardOptions.SIMCARD_PRODUCT,
      'by-technology': alarmGraphSimcardOptions.SIMCARD_TECHNOLOGY,
      'by-operator': alarmGraphSimcardOptions.SIMCARD_OPERATOR,
      'by-state': alarmGraphSimcardOptions.SIMCARD_STATE,
      'by-macroregion': alarmGraphSimcardOptions.SIMCARD_MACROREGIAO,
    };

    const transactionsGraphTypeMap = {
      'average-time': AlarmGraphAppNameOptions.APPNAME_AVERAGE_TIME,
      'transactions-by-status':
        AlarmGraphAppNameOptions.APPNAME_TRANSACTIONS_BY_STATUS,
      'transaction-history':
        AlarmGraphAppNameOptions.APPNAME_TRANSACTION_HISTORY,
      'transaction-status': AlarmGraphAppNameOptions.APPNAME_TRANSACTION_STATUS,
      'transactions-by-type':
        AlarmGraphAppNameOptions.APPNAME_TRANSACTIONS_BY_TYPE,
    };

    const isTransactionType = Object.values(TransactionsPayGraphTypes).includes(
      values.tipoGrafico as TransactionsPayGraphTypes,
    );

    const nomeGrafico = isTransactionType
      ? transactionsGraphTypeMap[
          values.tipoGrafico as keyof typeof transactionsGraphTypeMap
        ]
      : simcardsGraphTypeMap[
          values.tipoGrafico as keyof typeof simcardsGraphTypeMap
        ];

    const getAppNameFromFormValues = (): string | undefined => {
      if (!isTransactionType) return undefined;

      switch (values.tipoGrafico) {
        case TransactionsPayGraphTypes.AVERAGE_TIME:
          return values.averageTime;
        case TransactionsPayGraphTypes.TRANSACTIONS_BY_STATUS:
          return values.transactionByStatus;
        case TransactionsPayGraphTypes.TRANSACTION_HISTORY:
          return values.TransactionHistory;
        case TransactionsPayGraphTypes.TRANSACTION_STATUS:
          return values.transactionStatus;
        case TransactionsPayGraphTypes.TRANSACTIONS_BY_TYPE:
          return values.transactionByType;
        default:
          return undefined;
      }
    };

    const getStatusFromFormValues = (): string | undefined => {
      if (!isTransactionType) return undefined;

      switch (values.tipoGrafico) {
        case TransactionsPayGraphTypes.AVERAGE_TIME:
          return undefined;
        case TransactionsPayGraphTypes.TRANSACTIONS_BY_STATUS:
          return values.transactionByStatusStatus;
        case TransactionsPayGraphTypes.TRANSACTION_HISTORY:
          return values.transactionHistoryStatus;
        case TransactionsPayGraphTypes.TRANSACTION_STATUS:
          return values.transactionStatusStatus;
        case TransactionsPayGraphTypes.TRANSACTIONS_BY_TYPE:
          return values.transactionByTypeStatus;
        default:
          return undefined;
      }
    };

    const getQtdMaxValue = (): number | null => {
      switch (values.tipoAlarme) {
        case 'threshold':
          return values.thresholdMeasure === 'qtd_max'
            ? values.thresholdValue || null
            : null;
        case 'variacao_percentual':
          return values.percentVariationMeasure === 'perc_max'
            ? values.percentVariationValue || null
            : null;
        case 'sazonal':
          return values.seasonalMeasure === 'perc_max_sazonal'
            ? values.seasonalValue || null
            : null;
        default:
          return null;
      }
    };

    const getQtdMinValue = (): number | null => {
      switch (values.tipoAlarme) {
        case 'threshold':
          return values.thresholdMeasure === 'qtd_min'
            ? values.thresholdValue || null
            : null;
        case 'variacao_percentual':
          return values.percentVariationMeasure === 'perc_min'
            ? values.percentVariationValue || null
            : null;
        case 'sazonal':
          return values.seasonalMeasure === 'perc_min_sazonal'
            ? values.seasonalValue || null
            : null;
        default:
          return null;
      }
    };

    const basePayload = {
      nomeAlarme: values.nomeAlarme,
      nomeGrafico,
      qtdMax: getQtdMaxValue(),
      qtdMin: getQtdMinValue(),
      chatIds: values.notificationChannels?.includes('telegram')
        ? values.telegramIds
        : null,
      emails: values.notificationChannels?.includes('email')
        ? values.emails
        : null,
      tipoAlarme: values.tipoAlarme,
    };

    const payload: any = isTransactionType
      ? {
          ...basePayload,
          appName: getAppNameFromFormValues(),
          status: getStatusFromFormValues(),
        }
      : {
          ...basePayload,
          produtos: nomeGrafico === 'simcard_produto' ? values.produtos : null,
          operadoras:
            nomeGrafico === 'simcard_operadora' ? values.operadoras : null,
          tecnologias:
            nomeGrafico === 'simcard_tecnologia' ? values.tecnologias : null,
          estados: nomeGrafico === 'simcard_estado' ? values.estados : null,
          macroRegioes:
            nomeGrafico === 'simcard_macroregiao' ? values.macroregioes : null,
        };

    if (values.tipoAlarme === 'variacao_percentual') {
      payload.lastTime = values.percentVariationPeriod;
    } else if (values.tipoAlarme === 'sazonal') {
      payload.lastTime = '7d';
      payload.momento = values.seasonalMoment;
      payload.hora = values.seasonalTime?.hour();
      payload.minuto = values.seasonalTime?.minute();
    }

    if (nomeGrafico === 'transacao_tempo_medio') {
      payload.site = payload.appName;
    }

    if (nomeGrafico === 'transacao_por_status') {
      payload.site = payload.appName;
    }

    if (nomeGrafico === 'transacao_historico') {
      payload.site = payload.appName;
      payload.appName = payload.status;
    }

    if (nomeGrafico === 'transacao_de_status') {
      payload.site = payload.appName;
    }

    if (nomeGrafico === 'transacao_tipo_status') {
      payload.site = payload.appName;
    }

    return payload;
  };

  const { isLoading: isCreatingAlarm } = useSwr<CreateAlarmResponse>(
    submitData ? getAlarmEndpoint() : null,
    {
      method: 'POST',
      headers: {
        customerId: customer?.customerLogged?.id,
        customerName: customer?.customerLogged?.name,
      },
      data: submitData,
    },
    {
      onSuccess: () => {
        message.success(t('create-alarm-step:createAlarmSuccess'));
        setSubmitData(null);
        onAlarmCreated?.();
        handleClose();
      },
      onError: error => {
        const errorMessage =
          error.response?.data?.error || error.response?.data || '';
        const isDuplicateError =
          error.response?.status === 409 &&
          typeof errorMessage === 'string' &&
          errorMessage.toLowerCase().includes('already exists');

        if (isDuplicateError) {
          form.setFields([
            {
              name: 'nomeAlarme',
              errors: [t('create-alarm-step:createAlarmDuplicateError')],
            },
          ]);
        } else if (error.response?.data) {
          message.error(error.response.data);
        } else {
          message.error(t('create-alarm-step:createAlarmError'));
        }
        setSubmitData(null);
      },
    },
  );

  const handleFinish = async () => {
    try {
      const formValues = form.getFieldsValue();

      await form.validateFields([
        'nomeAlarme',
        'tipoAlarme',
        'notificationChannels',
      ]);

      const payload = buildPayload(formValues);

      setSubmitData(payload);
    } catch (error) {
      message.error(t('create-alarm-step:createAlarmFieldError'));
    }
  };

  const handleFinishFailed = () => {
    message.error(t('create-alarm-step:createAlarmErrorOnCreate'));
  };

  const items = steps.map(item => ({ key: item.title, title: item.title }));

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const contentStyle: React.CSSProperties = {
    lineHeight: '260px',
    textAlign: 'center',
    marginTop: 16,
  };

  return (
    <CustomModalWrapper
      title={t('create-alarm-step:createAlarmTitle')}
      bodyStyle={{ minHeight: 180, padding: 24 }}
      maskClosable={false}
      open={visible}
      centered
      onCancel={handleClose}
      width={600}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={handleClose}>
            {t('create-alarm-step:createAlarmCancel')}
          </Button>
          <div>
            {current > 0 && !skipToStepTwo && (
              <Button style={{ marginRight: 8 }} onClick={prev}>
                {t('create-alarm-step:createAlarmBack')}
              </Button>
            )}
            {current < steps.length - 1 && (
              <Button type="primary" onClick={next}>
                {t('create-alarm-step:createAlarmNext')}
              </Button>
            )}
            {current === steps.length - 1 && (
              <Button
                type="primary"
                onClick={handleFinish}
                loading={isCreatingAlarm}
              >
                {t('create-alarm-step:createAlarmConfirm')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <Form
        form={form}
        onFinish={handleFinish}
        onFinishFailed={handleFinishFailed}
        layout="vertical"
      >
        {hideSteps ? (
          <>
            <Divider
              style={{
                fontSize: '0.75rem',
                opacity: 0.7,
                textTransform: 'uppercase',
                margin: '0 0 16px 0',
              }}
            >
              {t('create-alarm-step:createAlarmPreDefinedData')}
            </Divider>
            <Row
              gutter={[16, 10]}
              style={{
                marginBottom: 24,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <CustomTag
                style={{
                  margin: 4,
                  padding: '2px 8px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                  {`${t('create-alarm-step:createAlarmSelectedGraph')}: `}
                </span>
                <span style={{ fontSize: '0.85rem', marginLeft: 4 }}>
                  {t('create-alarm-step:stepOne.createAlarmAverageTimeField')}
                </span>
              </CustomTag>
              <CustomTag
                style={{
                  margin: 4,
                  padding: '2px 8px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                  {`${t('create-alarm-step:createAlarmSelectedAppName')}: `}
                </span>
                <span style={{ fontSize: '0.85rem', marginLeft: 4 }}>
                  {preSelectedValues?.appName}
                </span>
              </CustomTag>
            </Row>
          </>
        ) : (
          <Steps current={current} items={items} />
        )}
        <div style={contentStyle}>
          <div style={{ display: current === 0 ? 'block' : 'none' }}>
            <CreateAlarmStepOne readOnlyFields={readOnlyFields} />
          </div>
          <div style={{ display: current === 1 ? 'block' : 'none' }}>
            <CreateAlarmStepTwo />
          </div>
        </div>
      </Form>
    </CustomModalWrapper>
  );
}
